"use client"

import { ArrowUp, ShieldCheck, X } from "lucide-react"
import { useEffect, useState } from "react"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

export function ScrollProgressIsland({ className }: { className?: string }) {
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    const updateProgress = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight
      setProgress(scrollable <= 0 ? 0 : Math.min(100, (window.scrollY / scrollable) * 100))
    }

    updateProgress()
    window.addEventListener("scroll", updateProgress, { passive: true })
    window.addEventListener("resize", updateProgress)
    return () => {
      window.removeEventListener("scroll", updateProgress)
      window.removeEventListener("resize", updateProgress)
    }
  }, [])

  return (
    <div className={cn("fixed inset-x-0 top-0 z-[60] h-1 bg-transparent", className)} aria-hidden="true">
      <div className="h-full bg-primary shadow-[0_0_24px_hsl(var(--primary)/0.45)] transition-[width] duration-150" style={{ width: `${progress}%` }} />
    </div>
  )
}

export function BackToTopIsland({ className }: { className?: string }) {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 760)
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  return (
    <Button
      type="button"
      size="icon"
      className={cn("fixed bottom-5 right-5 z-50 rounded-full shadow-2xl transition-all duration-300", visible ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-4 opacity-0", className)}
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      aria-label="Back to top"
    >
      <ArrowUp className="size-4" />
    </Button>
  )
}

export function ConsentBannerIsland({ storageKey = "hiveku-consent", className }: { storageKey?: string; className?: string }) {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    setVisible(window.localStorage.getItem(storageKey) !== "accepted")
  }, [storageKey])

  if (!visible) return null

  return (
    <div className={cn("fixed inset-x-4 bottom-4 z-50 mx-auto max-w-3xl rounded-[1.5rem] border border-border/70 bg-background/92 p-4 text-foreground shadow-2xl backdrop-blur-xl sm:flex sm:items-center sm:gap-4", className)} role="dialog" aria-label="Privacy preferences">
      <div className="flex flex-1 gap-3">
        <span className="mt-1 flex size-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground"><ShieldCheck className="size-4" /></span>
        <div>
          <p className="font-semibold">Privacy-aware analytics</p>
          <p className="mt-1 text-sm leading-6 text-muted-foreground">Use this banner when a generated site wires analytics, ads, or non-essential tracking.</p>
        </div>
      </div>
      <div className="mt-4 flex gap-2 sm:mt-0">
        <Button type="button" className="rounded-full" onClick={() => { window.localStorage.setItem(storageKey, "accepted"); setVisible(false) }}>Accept</Button>
        <Button type="button" size="icon" variant="ghost" className="rounded-full" onClick={() => setVisible(false)} aria-label="Dismiss"><X className="size-4" /></Button>
      </div>
    </div>
  )
}
